import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import {
  ButtonComponent,
  GridCellNameIconComponent,
  GridCellWithButtonComponent,
  GridModule,
  LoaderModule
} from '@mds/ui';
import { ConfirmationDialogComponent } from '@dialogs/confirmation-dialog/confirmation-dialog.component';
import { ToastrService } from '@services/toaster.service';
import { GetRowIdParams, GridApi } from 'ag-grid-community';
import { MakersiteService } from '@services/makersite.service';
import { GridOverrides } from '@mds/common';
import { ComplianceQuestionnaireEditComponent } from '../compliance-questionnaire/compliance-questionnaire-edit/compliance-questionnaire-edit.component';
import { catchError, forkJoin, of, Subject, switchMap, takeUntil } from 'rxjs';
import { InformationRequestService } from '@pages/information-requests/information-request.service';
import { EventDelegatorService } from '@services/event-delegator.service';
import { EditScipCategoryComponent } from './edit-scip-category/edit-scip-category.component';
import { ColorAliases } from '@mds/common';
import { CommonModule } from '@angular/common';
import { NoDataComponent } from '@components/no-data/no-data.component';
import { MenuService } from '@services/menu.service';
@Component({
  selector: 'app-scip-questionnaire',
  templateUrl: './scip-questionnaire.component.html',
  styleUrls: ['./scip-questionnaire.component.scss'],
  standalone: true,
  imports: [CommonModule, MdsIconsModule, GridModule, ButtonComponent, LoaderModule, NoDataComponent],
  providers: [InformationRequestService]
})
export class ScipQuestionnaireComponent implements OnInit {
  @Input() response: any;
  @Input() threads;
  @Input() rid: string;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  icons = MdsIcon;
  isDragging = false;
  uploadedFileName: string | null = null;
  payload: any;
  fileSelected = null;
  uploadInProgress = false;
  editable = false;
  userInfo = JSON.parse(localStorage.getItem('user_info'));
  isUserExternalSupplier = (this.userInfo?.features ?? [])
    .map((feature: { plan: string }) => feature.plan)
    .includes('external_supplier');
  reviewMode = false;
  gridApi: GridApi;
  gridOverrides: GridOverrides;
  private destroy$ = new Subject<void>();
  colors = ColorAliases;
  emptyData = {
    iconDetails: {
      name: MdsIcon.EMPTY_STREAM
    },
    title: 'No answer yet',
    subtitle: 'Please wait for the supplier to provide the information or get in touch to ask for updates'
  };

  constructor(
    public dialog: MatDialog,
    private makersiteService: MakersiteService,
    private informationRequestService: InformationRequestService,
    private toastrService: ToastrService,
    private eventDelegatorService: EventDelegatorService,
    private menuService: MenuService
  ) {}

  ngOnInit(): void {
    this.payload = {
      request_rid: this.response?.info?.rid,
      product_rid: this.response?.info?.request_product_rid
    };
    this.editable = this.response.actions.edit;
    if (this.isUserExternalSupplier) {
      this.emptyData.title = 'No compliance actions yet';
      this.emptyData.subtitle =
        'Please upload your product structure in the section above to start filling in the SCIP questionnaire';
    }

    this.createTableOverrides();
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
  }

  onFileDropped(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      this.fileSelected = event.dataTransfer.files[0];
    }
  }

  onFileSelected(event: any) {
    const file: File = event.target.files[0];
    if (file) {
      const allowedExtensions = ['xls', 'txt', 'csv', 'xlsx'];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      // Validate file extension
      if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
        this.clearFile();
        this.toastrService.show('Invalid file type. Please select a valid file.', 4000);
        return;
      }
      this.fileSelected = file;
      this.uploadedFileName = file.name;
      this.uploadFile();
    }
  }

  uploadFile() {
    if (this.fileSelected) {
      this.uploadInProgress = true;
      this.makersiteService.uploadScipBom(this.payload, this.fileSelected).subscribe({
        next: () => this.handleSuccess(),
        error: (err) => this.handleError(err)
      });
    }
  }

  handleSuccess() {
    this.toastrService.show('Bom Imported Successfully');
    this.uploadInProgress = false;
    this.clearFile();
    this.reload();
  }

  handleError(err) {
    this.uploadInProgress = false;
    this.toastrService.show(err.error.message);
    this.clearFile();
  }

  clearFile() {
    this.uploadedFileName = null;
    this.fileSelected = null;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  downloadBom() {
    const url = this.response.info.bom_file.link;
    const downloadElement = document.createElement('a');
    downloadElement.setAttribute('target', '_blank');
    downloadElement.setAttribute('href', url);
    document.body.appendChild(downloadElement);
    downloadElement.click();
    downloadElement.remove();
  }

  downloadTemplate() {
    this.makersiteService.getBomTemplate().subscribe((res: any) => {
      const link = document.createElement('a');
      link.href = res.link;
      link.download = 'bom_template.xlsx';
      link.target = '_blank';
      link.click();
    });
  }

  openLink() {
    window.open(
      'https://support.makersite.io/importing-data/how-to-prepare-bom-files?from_search=174465248&swifty_search_highlight=BOm',
      '_blank'
    );
  }

  deleteBom() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        type: 'Delete',
        header: 'Delete?',
        text: `Would you like to delete the bom?`,
        cancel_text: 'Cancel',
        delete_text: 'Delete'
      }
    });
    dialogRef.afterClosed().subscribe((confirm) => {
      if (confirm) {
        this.makersiteService.deleteBomTemplate(this.payload).subscribe((res) => {
          this.reload();
          this.toastrService.show('Deleted Successfully');
        });
      }
    });
  }

  private createTableOverrides() {
    const { lines } = this.response.tabs[0];
    this.editable = this.response.actions.edit;
    this.reviewMode = this.response.actions.close && !this.isUserExternalSupplier;
    if (!lines.length) {
      return;
    }
    const productColumn = lines[0].find((line) => line.key === 'name');
    this.gridOverrides = {
      columnDefs: this.getColumns(lines[0], this.response.additional_data.rsls),
      rowData: this.getRowData(lines),
      defaultColDef: {
        minWidth: 100,
        enableValue: true,
        enablePivot: true,
        sortable: true,
        filter: true,
        resizable: true
      },
      autoGroupColumnDef: {
        headerName: productColumn.label || 'Product',
        field: productColumn.key,
        minWidth: 350,
        tooltipField: productColumn.key,
        headerTooltip: productColumn.label || 'Product',
        cellRenderer: 'agGroupCellRenderer',
        lockPosition: true,
        pinned: 'left',
        cellRendererParams: {
          suppressCount: true,
          innerRenderer: GridCellNameIconComponent
        },
        filter: 'agTextColumnFilter'
      },
      groupDefaultExpanded: -1,
      headerHeight: 56,
      groupSelectsChildren: false,
      suppressRowClickSelection: true,
      suppressAggFuncInHeader: false,
      getRowHeight: () => 48,
      treeGrid: true,
      getDataPath: (data: any) => data.dataPath,
      getRowId: (params: GetRowIdParams) => String(params.data.rid)
    };
  }

  private getColumns(lines, rsls) {
    const columnDefs = [];
    const scipCategory = lines.find((line) => line.key === 'out_scip_article_category.in');
    // Add Product column if found
    if (scipCategory) {
      columnDefs.push({
        headerName: scipCategory.label || 'Scip Category',
        field: 'out_scip_article_category',
        width: 300,
        minWidth: 300,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        tooltipField: 'out_scip_article_category',
        headerTooltip: scipCategory.label || 'Scip Category',
        lockPosition: true,
        pinned: 'left',
        cellRenderer: GridCellWithButtonComponent,
        cellRendererParams: (params) => {
          const data: any = {
            editable: this.editable,
            onClick: () => (this.editable ? this.editCategory(params) : null),
            showEditIcon: true,
            placeholder: 'No category filled in'
          };
          return data;
        }
      });
    }
    lines.forEach((line) => {
      if (['name', 'out_scip_article_category.in'].includes(line.key)) return;
      const rslRid = rsls.find((rls) => rls.name === line.label).rid ?? '';
      const column = {
        headerName: line.label,
        field: line.key,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        tooltipField: line.key,
        headerTooltip: line.label,
        width: 300,
        minWidth: 300,
        headerComponentParams: {
          rid: rslRid,
          editable: this.editable
        },
        cellRenderer: GridCellWithButtonComponent,
        cellRendererParams: (params) => {
          const thread = this.getThread(params.data.rid, rslRid);
          const statement = params.data.lines.find((line) => line.key === `out_l_declaration_${rslRid}`);
          const data: any = {
            placeholder: params.data.locked ? '' : 'Select Compliance',
            rid: rslRid,
            locked: statement.declaration?.locked,
            editable: this.editable && !params.data.locked,
            attachments: statement.attachments.map((file) => file.name).join(' '),
            attachmentsCount: statement.attachments?.length || 0,
            onClick: () => false,
            showEditIcon: true
          };
          if (thread) {
            data.messageCount = thread?.unseen_count;
          }
          return data;
        },
        onCellClicked: (params) => {
          const statement = params.data.lines.find((line) => line.key === `out_l_declaration_${rslRid}`);
          if (!this.editable && !statement.declaration) return;
          const editable =
            this.editable && !params.data.locked && !params.data?.ipc_file_attached && !statement.declaration?.locked;
          params.node.setSelected(true, true);
          this.onEdit(params, editable);
        }
      };
      columnDefs.push(column);
    });
    return columnDefs;
  }

  private getRowData(productLineGroups: any[]): any[] {
    const nodeMap = new Map(
      productLineGroups.map((productLineGroup) => [productLineGroup[0].rid, productLineGroup[0]])
    );
    const buildDataPath = (node: any): string[] => {
      if (node?.parent_rid && nodeMap.get(node.parent_rid)) {
        return [...buildDataPath(nodeMap.get(node.parent_rid)), node.rid];
      }
      return [node.rid];
    };
    return productLineGroups.map((productLineGroup) => {
      const mainNode = productLineGroup[0];
      const rowData = productLineGroup.reduce(
        (rowItem, lineItem) => {
          if (lineItem.key === 'out_scip_article_category.in') {
            rowItem['out_scip_article_category'] = lineItem.value ?? null;
          } else {
            rowItem[lineItem.key] = lineItem.value ?? null;
          }
          return rowItem;
        },
        {} as Record<string, any>
      );
      return {
        ...rowData,
        ...mainNode,
        lines: productLineGroup,
        dataPath: buildDataPath(mainNode)
      };
    });
  }

  onEdit(gridParams, editable: boolean): void {
    if (!this.hasSelection() || this.isRowLocked(gridParams)) return;

    const context = this.buildEditContext(gridParams);
    if (!context) return;

    const dialogRef = this.openEditDialog(context, editable);
    this.handleDialogSubmit(dialogRef, context);
  }

  private hasSelection(): boolean {
    return !!this.gridApi.getSelectedRows().length;
  }

  private isRowLocked(gridParams): boolean {
    return !!gridParams?.data?.locked;
  }

  private buildEditContext(gridParams) {
    const selectedRslKey = gridParams?.column?.colId ?? '';
    const selectedRslRid = gridParams?.colDef?.headerComponentParams?.rid || gridParams?.rid;

    const selectedRsl = this.response.additional_data.rsls.find((rsl) => rsl.rid === selectedRslRid);

    if (!selectedRsl) return null;

    const row = gridParams.data;
    const line = row.lines.find((line) => line.key === selectedRslKey);
    if (!line || line.declaration?.locked) return null;

    return {
      selectedRslKey,
      selectedRsl,
      selectedRslRid,
      selectedProducts: [row],
      attachments: line.attachments ?? [],
      skuExemptions: line.exemptions ?? [],
      declaration: line.declaration ?? null,
      thread: this.getThreadIfSingle(row.rid, selectedRslRid)
    };
  }

  private getThreadIfSingle(productRid: string, rslRid: string) {
    return productRid ? this.getThread(productRid, rslRid) : null;
  }

  private openEditDialog(context, editable: boolean) {
    return this.dialog.open(ComplianceQuestionnaireEditComponent, {
      panelClass: 'dialog-panel',
      disableClose: true,
      closeOnNavigation: false,
      width: '800px',
      autoFocus: false,
      data: {
        editable,
        selectedRsl: { key: context.selectedRslKey, ...context.selectedRsl },
        selectedProducts: context.selectedProducts,
        attachments: context.attachments,
        sku_exemptions: context.skuExemptions,
        declaration: context.declaration,
        actions: this.response.tabs[0].actions,
        thread: context.thread
      }
    });
  }

  private handleDialogSubmit(dialogRef, context): void {
    dialogRef.componentInstance.submitDataEmitter
      .pipe(
        switchMap((data: any) => this.handleSubmit(data, context)),
        takeUntil(this.destroy$)
      )
      .subscribe((success) => this.onSaveSuccess(success, dialogRef));
  }

  private handleSubmit({ payload, attachments, deletedAttachments, comment }, context) {
    if (!payload) return of([]);
    return this.informationRequestService.saveRequest(this.rid, payload).pipe(
      switchMap((success) => this.buildSaveRequests(success, attachments, deletedAttachments, comment, context)),
      catchError((err) => {
        this.toastrService.show(err.error.message, 5000);
        return of([]);
      })
    );
  }

  private buildSaveRequests(success, attachments, deletedAttachments, comment, context) {
    const requests = [
      ...this.buildDeleteAttachmentRequests(deletedAttachments),
      ...this.buildUploadAttachmentRequests(attachments, context),
      ...this.buildCommentRequests(comment, context)
    ];
    return requests.length ? forkJoin(requests) : of([success]);
  }

  private buildDeleteAttachmentRequests(deletedAttachments: []) {
    return [this.informationRequestService.deleteProductAttachment(this.rid, deletedAttachments)];
  }

  private buildUploadAttachmentRequests(attachments = [], context) {
    if (!attachments.length) return [];
    return [
      this.informationRequestService.uploadRslAttachments(
        this.rid,
        context.selectedProducts.map((p) => p.rid),
        attachments,
        context.selectedRsl.rid
      )
    ];
  }

  private buildCommentRequests(comment, context) {
    if (!comment) return [];

    if (context.thread?.id) {
      return [this.makersiteService.addComment({ content: comment }, context.thread.id)];
    }

    return [
      this.makersiteService.createThread({
        message: { content: comment },
        tags: [
          `sku:${context.selectedProducts[0].rid}`,
          `regulation:${context.selectedRslRid}`,
          `questionnaire:#${this.rid}`
        ]
      })
    ];
  }

  private onSaveSuccess(success, dialogRef): void {
    if (!success?.length) return;

    this.toastrService.show('Saved Successfully', 2000);
    dialogRef.close();
    this.reload();
  }

  private reload() {
    this.menuService.setReloadState(true);
  }

  editCategory(params) {
    const dialogRef = this.dialog.open(EditScipCategoryComponent, {
      panelClass: 'dialog-panel',
      disableClose: true,
      closeOnNavigation: false,
      width: '500px',
      autoFocus: false,
      data: {
        requestRid: this.rid,
        ...params.data
      }
    });
    const instance = dialogRef.componentInstance;
    instance.submitDataEmitter.subscribe((response) => {
      dialogRef?.close();
      this.reload();
    });
  }

  onGridReady(gridApi: GridApi) {
    this.gridApi = gridApi;
  }

  private getThread(skuId, regulationId) {
    return this.threads?.find(
      (thread) => thread.metadata.sku_id === skuId && thread.metadata.regulation_id === regulationId
    );
  }
}
